import moment, { type Moment } from 'moment'

export function getMaxAllowedBudget(approvedTotal: number, priceBuffer: number): number {
  return approvedTotal + (approvedTotal * priceBuffer / 100)
}

export function isRouteMatchApprovalRequest(
  approvalRequest: App.BusinessTraveller.ApprovalRequest | undefined,
  flightItems: Array<App.Checkout.FlightItem>,
  flightViews: Array<App.Checkout.FlightItemView>,
): boolean {
  if (approvalRequest?.details?.flightItems && flightItems.length > 0 && flightViews.length > 0) {
    const departureRouteFromApprovalRequest = approvalRequest?.details?.flightItems.flatMap(item => item.flightGroup.flights.map(flight => `${flight.departureAirport}-${flight.arrivalAirport}`))
    const selectedDepartureRoutes = flightViews.flatMap(view => view.flights.flatMap(flight => flight.journeyFlight.flights.map(flight => `${flight.departingAirport}-${flight.arrivalAirport}`)))
    return departureRouteFromApprovalRequest.every((route, index) => selectedDepartureRoutes[index] === route)
  }

  return true
}

export function isAllDatesMatchApprovalRequest(
  approvalRequest: App.BusinessTraveller.ApprovalRequest | undefined,
  flightItems: Array<App.Checkout.FlightItem>,
  flightViews: Array<App.Checkout.FlightItemView>,
): boolean {
  if (approvalRequest?.details?.flightItems && flightItems.length > 0 && flightViews.length > 0) {
    const departureDatesFromApprovalRequest = approvalRequest?.details?.flightItems.flatMap(item => item.flightGroup.flights.map(flight => flight.departingDate))
    const selectedDepartureDates = flightViews.flatMap(view => view.flights.flatMap(flight => flight.journeyFlight.flights.map(flight => flight.departingDate)))

    return departureDatesFromApprovalRequest.every((date, index) => selectedDepartureDates[index] === date)
  }

  return true
}

interface SearchRequestSegment {
  departureAirport?: {
    airportCode?: string
  }
  arrivalAirport?: {
    airportCode?: string
  }
  checkinDate?: Moment
  checkoutDate?: Moment
}

export function isDateAndAirportsMatchApprovalRequest(
  approvalRequest: App.BusinessTraveller.ApprovalRequest | undefined,
  segment?: SearchRequestSegment | undefined,
): boolean {
  if (segment?.checkinDate && approvalRequest?.details?.flightItems && moment.isMoment(segment.checkinDate) && segment.checkinDate.isValid()) {
    // flightGroup.flights[] holds one entry per leg, so collapse to overall origin/destination + first-leg date to match stopover bookings.
    const itemSummaries = approvalRequest.details.flightItems.map((item) => {
      const flights = item.flightGroup.flights
      return {
        departureAirport: flights[0]?.departureAirport,
        arrivalAirport: flights.at(-1)?.arrivalAirport,
        departingDate: flights[0]?.departingDate,
      }
    })

    const segmentDepartDate = segment.checkinDate.format('YYYY-MM-DD')
    const segmentReturnDate = segment.checkoutDate && moment.isMoment(segment.checkoutDate) && segment.checkoutDate.isValid()
      ? segment.checkoutDate.format('YYYY-MM-DD')
      : undefined

    // For return trips, also require the return date to match an approved item's depart date.
    if (segmentReturnDate && itemSummaries.every(summary => summary.departingDate !== segmentReturnDate)) {
      return false
    }

    // If no airport codes provided, just rely on the date match
    if (!segment.departureAirport?.airportCode || !segment.arrivalAirport?.airportCode) {
      return itemSummaries.some(summary => summary.departingDate === segmentDepartDate)
    }

    // Date and airports must match on the same flight item — otherwise an inbound's date
    // paired with an outbound's route would falsely match for a return trip.
    return itemSummaries.some(
      summary =>
        summary.departingDate === segmentDepartDate &&
        summary.departureAirport === segment.departureAirport?.airportCode &&
        summary.arrivalAirport === segment.arrivalAirport?.airportCode,
    )
  }

  return false
}

/**
 * Finds the approved car in search results by exact offer ID.
 * approvedOfferIdFromUrl is used when coming from "Proceed with booking" (passed via URL).
 */
export function findApprovedCarOfferInSearchResults(
  approvalRequest: App.BusinessTraveller.ApprovalRequest | undefined,
  offers: Array<App.CarHireOffer>,
  searchParams: { pickUpId?: string; dropOffId?: string; pickUpDate?: string; dropOffDate?: string },
  approvedOfferIdFromUrl?: string,
): App.CarHireOffer | undefined {
  const carHireItem = approvalRequest?.tripRequestItems?.find(
    (item): item is App.BusinessTraveller.CarHireTripRequestItem => item.type === 'car_hire',
  )
  if (!carHireItem || approvalRequest?.status !== 'APPROVED') return undefined
  if (!isCarHireSearchMatchApprovalRequest(approvalRequest, searchParams)) return undefined

  const approvedOfferId = approvedOfferIdFromUrl ?? carHireItem.displayData?.carHireDetails?.offerId
  return approvedOfferId ? offers.find(o => o.id === approvedOfferId) : undefined
}

export function shouldPreserveCarHireApprovalRequestForOffer(
  approvalRequest: App.BusinessTraveller.ApprovalRequest | undefined,
  offer: App.CarHireOffer,
  searchParams: { pickUpId?: string; dropOffId?: string; pickUpDate?: string; dropOffDate?: string },
  approvedOfferIdFromUrl?: string,
): boolean {
  const carHireItem = approvalRequest?.tripRequestItems?.find(
    (item): item is App.BusinessTraveller.CarHireTripRequestItem => item.type === 'car_hire',
  )
  if (!carHireItem || approvalRequest?.status !== 'APPROVED') return false
  if (!isCarHireSearchMatchApprovalRequest(approvalRequest, searchParams)) return false

  const approvedOfferId = approvedOfferIdFromUrl ?? carHireItem.displayData?.carHireDetails?.offerId
  return !!approvedOfferId && offer.id === approvedOfferId
}

/**
 * Returns true if the car hire search params match the approved trip request.
 * Used to show the pinned car only when the user is on the correct search.
 */
export function isCarHireSearchMatchApprovalRequest(
  approvalRequest: App.BusinessTraveller.ApprovalRequest | undefined,
  searchParams: { pickUpId?: string; dropOffId?: string; pickUpDate?: string; dropOffDate?: string },
): boolean {
  const carHireItem = approvalRequest?.tripRequestItems?.find(
    (item): item is App.BusinessTraveller.CarHireTripRequestItem => item.type === 'car_hire',
  )
  const details = carHireItem?.displayData?.carHireDetails
  const pickupDate = carHireItem?.displayData?.pickupDate
  const dropoffDate = carHireItem?.displayData?.dropoffDate

  if (!details?.pickUpId || !details?.dropOffId || !pickupDate || !dropoffDate) return false

  return (
    details.pickUpId === searchParams.pickUpId &&
    details.dropOffId === searchParams.dropOffId &&
    pickupDate === searchParams.pickUpDate &&
    dropoffDate === searchParams.dropOffDate
  )
}
